import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { GraduationCap, Radio, ShieldCheck, ShieldAlert, CheckCircle2, XCircle, FlaskConical, Send, Upload } from 'lucide-react';
import { studioApi } from '@/coach/studioApi';
import { startRecording } from '@/coach/recorder';
import { useAuth } from '@/contexts/AuthContext';

const TARGETS = [
  'reports-new-insight-build',
  'report-visual-canvas',
  'dashboards-share-button',
  'reports-filter-panel',
  'reports-empty-state',
];
const CONF = { high: 'text-emerald-500', medium: 'text-amber-500', low: 'text-rose-500' };
const STATUS_STYLE = {
  draft: 'bg-slate-500/15 text-slate-400', in_review: 'bg-amber-500/15 text-amber-500',
  approved: 'bg-blue-500/15 text-blue-400', published: 'bg-emerald-500/15 text-emerald-500',
  rejected: 'bg-rose-500/15 text-rose-500',
};

export default function CoachStudioPage() {
  const { hasPermission } = useAuth();
  const canApprove = hasPermission ? hasPermission('governance:manage') : false;
  const [params, setParams] = useSearchParams();
  const [drafts, setDrafts] = useState([]);
  const [goal, setGoal] = useState('');
  const [recordingId, setRecordingId] = useState(params.get('recording') || null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null); // full draft

  const refresh = useCallback(async () => {
    try { setDrafts((await studioApi.list()).drafts || []); }
    catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (params.get('recording')) { setRecordingId(params.get('recording')); toast.success('Recording captured — add a goal and generate a draft'); } }, [params]);

  const open = async (id) => { try { setSel(await studioApi.get(id)); } catch (e) { toast.error(e.message); } };

  const generate = async () => {
    if (!goal.trim()) { toast.error('Enter a goal'); return; }
    setBusy(true);
    try {
      const d = await studioApi.generate(goal, recordingId);
      toast.success('Draft generated (non-authoritative)');
      setGoal(''); setRecordingId(null); setParams({});
      await refresh(); setSel(d);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const record = () => { startRecording(goal); toast.message('Recording started — demonstrate the workflow, then Stop'); };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await open(sel.draft_id); await refresh(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const patchStep = (idx, key, val) => {
    const steps = sel.steps.map((s, i) => i === idx ? { ...s, [key]: val } : s);
    setSel({ ...sel, steps });
  };
  const patchPredicate = (idx, kind, value) => {
    const pred = kind === 'state' ? { state: { route_contains: value || '/' } } : { event_type: value || 'widget_added' };
    patchStep(idx, 'completion_predicate', pred);
  };
  const saveEdits = () => act(async () => {
    const steps = sel.steps.map((s) => ({
      step_id: s.step_id, title: s.title, instruction_text: s.instruction_text,
      semantic_target_id: s.semantic_target_id, safe_actions: s.safe_actions || ['highlight'],
      completion_predicate: s.completion_predicate,
    }));
    await studioApi.edit(sel.draft_id, { title: sel.title, steps });
  }, 'Saved (approval reset — re-validate, test, and approve)');

  return (
    <div className="p-6 space-y-5" data-testid="coach-studio-page">
      <div className="flex items-center gap-2">
        <GraduationCap className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold text-foreground">Coach Studio</h1>
        <span className="text-xs text-muted-foreground ml-2">Author guided walkthroughs from a goal + a recorded demonstration. Drafts are non-authoritative until approved &amp; published.</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: create + list */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-border/50 p-4 space-y-3" data-testid="studio-create">
            <div className="text-sm font-semibold">New draft</div>
            <textarea data-testid="studio-goal" value={goal} onChange={(e) => setGoal(e.target.value)} rows={3}
              placeholder="Goal, e.g. Guide a new analyst to add a chart to a report"
              className="w-full text-sm px-3 py-2 rounded-lg bg-muted/30 border border-border/40 outline-none focus:border-primary/40" />
            <div className="flex items-center gap-2">
              <button data-testid="studio-record" onClick={record}
                className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border/50 hover:bg-muted/40">
                <Radio className="w-3.5 h-3.5" /> Record demonstration
              </button>
              {recordingId && <span className="text-[11px] text-emerald-500" data-testid="studio-recording-attached">recording attached</span>}
            </div>
            <button data-testid="studio-generate" onClick={generate} disabled={busy}
              className="w-full text-sm font-medium px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {busy ? 'Generating…' : 'Generate draft with AI'}
            </button>
            <p className="text-[10px] text-muted-foreground">Uses the approved AI Gateway with strict schema validation. Disabled unless the global model switch is on.</p>
          </div>

          <div className="rounded-2xl border border-border/50 p-4" data-testid="studio-list">
            <div className="text-sm font-semibold mb-2">Drafts</div>
            {drafts.length === 0 ? <p className="text-xs text-muted-foreground">No drafts yet.</p> : (
              <div className="space-y-1.5">
                {drafts.map((d) => (
                  <button key={d.draft_id} data-testid={`studio-draft-${d.draft_id}`} onClick={() => open(d.draft_id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border hover:bg-muted/40 ${sel?.draft_id === d.draft_id ? 'border-primary/50' : 'border-border/40'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm truncate">{d.title}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_STYLE[d.status] || ''}`}>{d.status}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: editor */}
        <div className="lg:col-span-2">
          {!sel ? (
            <div className="rounded-2xl border border-dashed border-border/50 p-10 text-center text-sm text-muted-foreground" data-testid="studio-empty">
              Select or generate a draft to edit, validate, test, and publish.
            </div>
          ) : (
            <div className="rounded-2xl border border-border/50 p-4 space-y-4" data-testid="studio-editor">
              {/* Authority banner */}
              {sel.authoritative ? (
                <div className="flex items-center gap-2 text-sm text-emerald-500 bg-emerald-500/10 rounded-lg px-3 py-2" data-testid="studio-authoritative">
                  <ShieldCheck className="w-4 h-4" /> Published &amp; authoritative
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-amber-500 bg-amber-500/10 rounded-lg px-3 py-2" data-testid="studio-watermark">
                  <ShieldAlert className="w-4 h-4" /> {sel.watermark}
                </div>
              )}

              <div className="flex items-center justify-between gap-2">
                <input data-testid="studio-title" value={sel.title} disabled={sel.status === 'published'}
                  onChange={(e) => setSel({ ...sel, title: e.target.value })}
                  className="flex-1 text-lg font-semibold bg-transparent outline-none border-b border-transparent focus:border-primary/40" />
                <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_STYLE[sel.status]}`} data-testid="studio-status">{sel.status}</span>
              </div>
              {sel.provenance?.injection_flagged && (
                <div className="text-[11px] text-rose-500" data-testid="studio-injection-flag">⚠ Potential injection phrase detected in the goal — review carefully.</div>
              )}

              {/* Steps */}
              <div className="space-y-3">
                {sel.steps.map((s, i) => (
                  <div key={s.step_id} className="rounded-xl border border-border/40 p-3 space-y-2" data-testid={`studio-step-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Step {i + 1} · {s.step_id}</span>
                      <span className={`text-[11px] font-medium ${CONF[s.confidence] || ''}`} data-testid={`studio-confidence-${i}`}>
                        confidence: {s.confidence} · evidence: {(s.evidence || []).length}
                      </span>
                    </div>
                    <input data-testid={`studio-step-title-${i}`} value={s.title} disabled={sel.status === 'published'}
                      onChange={(e) => patchStep(i, 'title', e.target.value)}
                      className="w-full text-sm px-2 py-1 rounded bg-muted/30 border border-border/40 outline-none" />
                    <textarea data-testid={`studio-step-instr-${i}`} value={s.instruction_text} disabled={sel.status === 'published'} rows={2}
                      onChange={(e) => patchStep(i, 'instruction_text', e.target.value)}
                      className="w-full text-[13px] px-2 py-1 rounded bg-muted/30 border border-border/40 outline-none" />
                    <div className="flex flex-wrap items-center gap-2">
                      <select data-testid={`studio-step-target-${i}`} value={s.semantic_target_id} disabled={sel.status === 'published'}
                        onChange={(e) => patchStep(i, 'semantic_target_id', e.target.value)}
                        className="text-xs px-2 py-1 rounded bg-muted/30 border border-border/40">
                        {!TARGETS.includes(s.semantic_target_id) && <option value={s.semantic_target_id}>{s.semantic_target_id} (unknown)</option>}
                        {TARGETS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <select data-testid={`studio-step-predkind-${i}`} disabled={sel.status === 'published'}
                        value={s.completion_predicate?.state ? 'state' : 'event'}
                        onChange={(e) => patchPredicate(i, e.target.value, e.target.value === 'state' ? '/builder' : 'widget_added')}
                        className="text-xs px-2 py-1 rounded bg-muted/30 border border-border/40">
                        <option value="event">event predicate</option>
                        <option value="state">state predicate</option>
                      </select>
                      {s.completion_predicate?.state ? (
                        <input data-testid={`studio-step-predval-${i}`} value={s.completion_predicate.state.route_contains || ''} disabled={sel.status === 'published'}
                          onChange={(e) => patchPredicate(i, 'state', e.target.value)} placeholder="route contains…"
                          className="text-xs px-2 py-1 rounded bg-muted/30 border border-border/40" />
                      ) : (
                        <select data-testid={`studio-step-predval-${i}`} value={s.completion_predicate?.event_type || 'widget_added'} disabled={sel.status === 'published'}
                          onChange={(e) => patchPredicate(i, 'event', e.target.value)}
                          className="text-xs px-2 py-1 rounded bg-muted/30 border border-border/40">
                          {['widget_added', 'route_entered', 'builder_opened'].map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Validation / test results */}
              {sel.validation && (
                <div className="text-xs" data-testid="studio-validation">
                  {sel.validation.ok ? <span className="text-emerald-500 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Validation passed</span>
                    : <span className="text-rose-500 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Validation failed: {sel.validation.steps.flatMap((c) => c.issues).join('; ')}</span>}
                </div>
              )}
              {sel.test_results && (
                <div className="text-xs" data-testid="studio-test">
                  {sel.test_results.ok ? <span className="text-emerald-500 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Test mode passed (deterministic)</span>
                    : <span className="text-rose-500 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Test mode failed</span>}
                </div>
              )}

              {/* Actions */}
              {sel.status !== 'published' && (
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/40">
                  <button data-testid="studio-save" onClick={saveEdits} disabled={busy}
                    className="text-xs px-3 py-1.5 rounded-lg border border-border/50 hover:bg-muted/40">Save edits</button>
                  <button data-testid="studio-validate" onClick={() => act(() => studioApi.validate(sel.draft_id), 'Validated')} disabled={busy}
                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border/50 hover:bg-muted/40"><ShieldCheck className="w-3.5 h-3.5" /> Validate</button>
                  <button data-testid="studio-testbtn" onClick={() => act(() => studioApi.test(sel.draft_id), 'Tested')} disabled={busy}
                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border/50 hover:bg-muted/40"><FlaskConical className="w-3.5 h-3.5" /> Test mode</button>
                  {(sel.status === 'draft' || sel.status === 'rejected') && (
                    <button data-testid="studio-submit" onClick={() => act(() => studioApi.submit(sel.draft_id), 'Submitted for review')} disabled={busy}
                      className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-500 hover:bg-amber-500/30"><Send className="w-3.5 h-3.5" /> Submit for review</button>
                  )}
                  {canApprove && sel.status === 'in_review' && (
                    <>
                      <button data-testid="studio-approve" onClick={() => act(() => studioApi.approve(sel.draft_id), 'Approved')} disabled={busy}
                        className="text-xs px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30">Approve</button>
                      <button data-testid="studio-reject" onClick={() => act(() => studioApi.reject(sel.draft_id, 'needs changes'), 'Rejected')} disabled={busy}
                        className="text-xs px-3 py-1.5 rounded-lg bg-rose-500/20 text-rose-500 hover:bg-rose-500/30">Reject</button>
                    </>
                  )}
                  {sel.status === 'approved' && (
                    <button data-testid="studio-publish" onClick={() => act(() => studioApi.publish(sel.draft_id), 'Published')} disabled={busy}
                      className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-500 hover:bg-emerald-500/30"><Upload className="w-3.5 h-3.5" /> Publish</button>
                  )}
                </div>
              )}
              {sel.approvals?.length > 0 && (
                <p className="text-[11px] text-muted-foreground" data-testid="studio-approvals">Approved by {sel.approvals.map((a) => a.role).join(', ')}</p>
              )}
              {sel.status === 'published' && (
                <button data-testid="studio-revise" disabled={busy}
                  onClick={() => act(async () => { const d = await studioApi.revise(sel.walkthrough_id); setSel(d); }, 'Revision draft created — edit and re-approve to publish a new version')}
                  className="text-xs px-3 py-1.5 rounded-lg border border-border/50 hover:bg-muted/40">Create revision (new version)</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
